/**
 * The plan's space type (garage, workshop, bathroom …), picked by icon.
 *
 *   SpaceMenu   — the header button: the current space's icon and "Space",
 *                 opening a list of the spaces.
 *   SpacePicker — the grid of icon tiles in the room dialog.
 *
 * The spaces are the estimator's own (data/presets SPACE_TYPES, with their
 * lucide icons); their ids are the staging kits' keys, so a choice here is
 * the kit the room is furnished with.
 */

import React from 'react';
import { SPACE_TYPES } from '../../../data/presets';
import { spaceIcon } from '../../../data/spaceIcons';
import { allowedSpaces } from '../../../data/spaceAccess';
import { useAppSettings } from '../../../context/AppSettingsContext';
import { DESIGNER_STRINGS as S } from '../strings';
import { DesignerMenu, DesignerMenuItem } from './DesignerMenu';
import './designerHeader.css';

export const spaceLabel = (id: string): string => S.spaceTypeName[id] ?? SPACE_TYPES.find((s) => s.id === id)?.label ?? id;

export const SpaceIcon: React.FC<{ id: string; size?: number }> = ({ id, size = 14 }) => {
  const Icon = spaceIcon(SPACE_TYPES.find((s) => s.id === id)?.icon);
  return <Icon size={size} />;
};

export interface SpaceMenuProps {
  value: string;
  onChange: (id: string) => void;
}

/** The space types the admin offers in Advanced mode, and `keep` (the one chosen) whatever its switch says. */
const useAdvancedSpaces = (keep: string) => {
  const { settings } = useAppSettings();
  return allowedSpaces(SPACE_TYPES, settings.spaceTypeAccess, 'Advanced', keep);
};

export const SpaceMenu: React.FC<SpaceMenuProps> = ({ value, onChange }) => {
  const spaces = useAdvancedSpaces(value);
  return (
  <DesignerMenu icon={<SpaceIcon id={value} />} label={S.spaceType} title={`${S.spaceType}: ${spaceLabel(value)}`} menuLabel={S.spaceType}>
    {(close) => spaces.map((s) => (
      <DesignerMenuItem
        key={s.id}
        icon={<SpaceIcon id={s.id} />}
        label={spaceLabel(s.id)}
        active={s.id === value}
        onClick={() => { close(); if (s.id !== value) onChange(s.id); }}
      />
    ))}
  </DesignerMenu>
  );
};

export interface SpacePickerProps {
  value: string;
  onChange: (id: string) => void;
  ariaLabel?: string;
}

export const SpacePicker: React.FC<SpacePickerProps> = ({ value, onChange, ariaLabel }) => {
  const spaces = useAdvancedSpaces(value);
  return (
  <div className="dh-space-grid" role="radiogroup" aria-label={ariaLabel ?? S.spaceType}>
    {spaces.map((s) => (
      <button
        key={s.id}
        type="button"
        role="radio"
        aria-checked={s.id === value}
        className={`dh-space-tile${s.id === value ? ' is-active' : ''}`}
        title={spaceLabel(s.id)}
        onClick={() => onChange(s.id)}
      >
        <SpaceIcon id={s.id} size={18} />
        <span>{spaceLabel(s.id)}</span>
      </button>
    ))}
  </div>
  );
};

export default SpaceMenu;
